import type React from 'react';
import clsx from 'clsx';
import { useVulnerabilityData } from './hooks/useVulnerabilityData';
import { VulnerabilityFilters } from './VulnerabilityFilters';
import { VulnerabilityStatsGrid } from './VulnerabilityStatsGrid';
import { VulnerabilityTable } from './VulnerabilityTable';
import styles from './VulnerabilityManagement.module.css';

interface VulnerabilityManagementProps {
  onOpenRightDrawer?: (content: { title: string; component: React.ReactNode }) => void;
}

export const VulnerabilityManagement: React.FC<VulnerabilityManagementProps> = () => {
  const {
    navigate,
    loading,
    stats,
    searchTerm,
    severityFilter,
    statusFilter,
    falsePositivesOnly,
    hasActiveFilters,
    filteredVulnerabilities,
    paginatedVulnerabilities,
    selectedItems,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    indexOfFirstItem,
    indexOfLastItem,
    setSearchTerm,
    setSeverityFilter,
    setStatusFilter,
    setFalsePositivesOnly,
    setCurrentPage,
    handleSort,
    handleSelectItem,
    handleSelectAll,
    handleBulkFalsePositive,
    resetFilters,
    openDetailPage,
  } = useVulnerabilityData();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Vulnerability Management</h1>
      </div>

      <VulnerabilityStatsGrid stats={stats} />

      <VulnerabilityFilters
        searchTerm={searchTerm}
        severityFilter={severityFilter}
        statusFilter={statusFilter}
        falsePositivesOnly={falsePositivesOnly}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setCurrentPage(1);
        }}
        onSeverityChange={(value) => {
          setSeverityFilter(value);
          setCurrentPage(1);
        }}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        onFalsePositiveChange={(value) => {
          setFalsePositivesOnly(value);
          setCurrentPage(1);
        }}
        onResetFilters={resetFilters}
      />

      <div className={styles.controlsBar}>
        <div className={styles.resultCount}>
          {filteredVulnerabilities.length} vulnerabilit{filteredVulnerabilities.length === 1 ? 'y' : 'ies'}
          {hasActiveFilters ? ' (filtered)' : ''}
        </div>
        <button
          className={clsx(styles.button, styles.buttonSecondary)}
          onClick={handleBulkFalsePositive}
          disabled={selectedItems.length === 0}
        >
          Mark as False Positive ({selectedItems.length})
        </button>
      </div>

      {loading ? (
        <div className={styles.emptyState}>
          <h3>Loading...</h3>
          <p>Please wait while we load the vulnerabilities</p>
        </div>
      ) : filteredVulnerabilities.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No vulnerabilities found</h3>
          <p>Try adjusting your filters or run a security scan</p>
        </div>
      ) : (
        <VulnerabilityTable
          vulnerabilities={paginatedVulnerabilities}
          filteredCount={filteredVulnerabilities.length}
          selectedItems={selectedItems}
          sortField={sortField}
          sortDirection={sortDirection}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          onSort={handleSort}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          onOpenDetail={openDetailPage}
          onPageChange={setCurrentPage}
          onNavigate={navigate}
        />
      )}
    </div>
  );
};
